// ── ADVANCD shared UI primitives ──────────────────────────────────────────
// Token-driven, theme-aware building blocks. Colours come from the CSS custom
// properties in index.css (which track <html data-theme>), so these need NO
// theme/UI prop — drop them in anywhere. Scales come from tokens.js.
//
// These intentionally MATCH the app's existing look (the .btn-p/.btn-g/.btn-d
// classes, the standard input/card styling) so migrating an inline element to
// a primitive is a visual no-op. Adopt incrementally.
//
//   import { Button, Input, Textarea, Card, Badge, EmptyState, SectionHeader } from "./ui.jsx";

import { TOK } from "./tokens.js";

const FF = "'Helvetica Neue', Helvetica, Arial, sans-serif";

// ── Button ────────────────────────────────────────────────────────────────
// variant: "primary" (amber fill) | "secondary" (ghost) | "danger"
// size:    "sm" | "md"
const BTN_BASE = {
  fontFamily: FF,
  borderRadius: TOK.radius.xs,
  fontWeight: TOK.weight.heading,
  letterSpacing: "1px",
  cursor: "pointer",
  transition: `background var(--motion-fast, 120ms), border-color var(--motion-fast, 120ms), color var(--motion-fast, 120ms)`,
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: TOK.space[2],
};
const BTN_SIZE = {
  sm: { padding: "7px 14px", fontSize: TOK.text.xs },
  md: { padding: "9px 20px", fontSize: TOK.text.xs },
};
const BTN_VARIANT = {
  primary:   { background: "var(--accent)", color: "var(--accent-fg)", border: "1px solid transparent" },
  secondary: { background: "none", color: "var(--text-mut)", border: "1px solid var(--panel-border)" },
  danger:    { background: "none", color: "var(--danger)", border: "1px solid var(--danger)" },
};
export function Button({ variant = "primary", size = "md", style, disabled, children, ...rest }) {
  return (
    <button
      disabled={disabled}
      style={{
        ...BTN_BASE,
        ...BTN_SIZE[size],
        ...BTN_VARIANT[variant],
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

// ── Input / Textarea ────────────────────────────────────────────────────────
const FIELD_BASE = {
  width: "100%",
  boxSizing: "border-box",
  background: "var(--panel-alt)",
  border: "1px solid var(--field-border)",
  borderRadius: TOK.radius.xs,
  padding: "7px 10px",
  color: "var(--text)",
  fontFamily: FF,
  fontSize: TOK.text.sm,
  outline: "none",
  transition: "border-color var(--motion-fast, 120ms)",
};
export function Input({ style, ...rest }) {
  return <input style={{ ...FIELD_BASE, ...style }} {...rest} />;
}
export function Textarea({ style, rows = 3, ...rest }) {
  return <textarea rows={rows} style={{ ...FIELD_BASE, resize: "vertical", minHeight: 52, ...style }} {...rest} />;
}

// ── Card ────────────────────────────────────────────────────────────────────
export function Card({ style, children, ...rest }) {
  return (
    <div
      style={{
        background: "var(--panel)",
        border: "1px solid var(--panel-border)",
        borderRadius: TOK.radius.md,
        padding: TOK.space[4],
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  );
}

// ── Badge ─────────────────────────────────────────────────────────────────
// tone: "neutral" | "accent" | "success" | "danger" | "accent2"
const BADGE_TONE = {
  neutral: { color: "var(--text-mut)", bg: "var(--panel-alt)", bdr: "var(--panel-border)" },
  accent:  { color: "var(--accent)",   bg: "transparent",      bdr: "var(--accent)" },
  success: { color: "var(--success)",  bg: "transparent",      bdr: "var(--success)" },
  danger:  { color: "var(--danger)",   bg: "transparent",      bdr: "var(--danger)" },
  accent2: { color: "var(--accent-2)", bg: "transparent",      bdr: "var(--accent-2)" },
};
export function Badge({ tone = "neutral", style, children, ...rest }) {
  const t = BADGE_TONE[tone] || BADGE_TONE.neutral;
  return (
    <span
      style={{
        fontFamily: FF,
        fontSize: TOK.text.xs,
        fontWeight: TOK.weight.label,
        letterSpacing: "0.5px",
        textTransform: "uppercase",
        color: t.color,
        background: t.bg,
        border: `1px solid ${t.bdr}`,
        borderRadius: TOK.radius.xs,
        padding: "2px 7px",
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        whiteSpace: "nowrap",
        ...style,
      }}
      {...rest}
    >
      {children}
    </span>
  );
}

// ── SectionHeader ───────────────────────────────────────────────────────────
export function SectionHeader({ children, right, style, ...rest }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: TOK.space[3],
        fontFamily: FF,
        fontSize: TOK.text.xs,
        fontWeight: TOK.weight.label,
        letterSpacing: "1.5px",
        textTransform: "uppercase",
        color: "var(--text-mid)",
        ...style,
      }}
      {...rest}
    >
      <span>{children}</span>
      {right != null && <span>{right}</span>}
    </div>
  );
}

// ── EmptyState ──────────────────────────────────────────────────────────────
// One consistent empty pattern: dimmed icon (0.25), a line of guidance, optional action.
export function EmptyState({ icon, title, hint, action, style, ...rest }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        padding: "40px 20px",
        gap: TOK.space[2],
        ...style,
      }}
      {...rest}
    >
      {icon && <div style={{ opacity: 0.25, color: "var(--text-mut)" }}>{icon}</div>}
      {title && (
        <div style={{ fontFamily: FF, fontSize: TOK.text.base, color: "var(--text-mut)" }}>{title}</div>
      )}
      {hint && (
        <div style={{ fontFamily: FF, fontSize: TOK.text.xs, color: "var(--text-faint)", lineHeight: 1.6, maxWidth: 280 }}>{hint}</div>
      )}
      {action && <div style={{ marginTop: TOK.space[2] }}>{action}</div>}
    </div>
  );
}
